import React, { FunctionComponent, useEffect, useState, MouseEvent } from 'react';
import { useHistory } from 'react-router';
import { Box, Button, Fab, Tooltip } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import BackToTopArrow from '@material-ui/icons/ArrowUpward';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import InfiniteScrollTableView from '../../../components/InfiniteScrollTableView';
import Loader from '../../../components/Loader';
import { BASE_URL } from '../../../routes';
import TableEmptyState from '../../../components/TableView/TableEmptyState';
import DeleteDialog from '../../../components/DeleteDialog';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import Modal from '../../../components/Modal';
import { Order, IColumn } from '../../../store/modules/application/types';
import { InboxCountType, InboxList, UpdateInboxStatus } from 'src/store/modules/inbox/types';

declare module '@material-ui/core/styles/withStyles' {
    // Augment the BaseCSSProperties so that we can control jss-rtl
    interface BaseCSSProperties {
        /*
         * Used to control if the rule-set should be affected by rtl transformation
         */
        flip?: boolean;
    }
}

type InboxInfiniteListProps = {
    inboxList: InboxList;
    isLoading: boolean;
    skip: number;
    setSkip: Function;
    limit: number;
    hasMore: boolean;
    inboxCount: InboxCountType;
    sortOrder: Order;
    sortBy: string;
    setSortOrder: Function;
    setSortColumn: Function;
    updateInboxStatus: Function;
};

const componentStyles = makeStyles(() =>
    createStyles({
        root: {
            padding: '0px 40px 40px 40px',
        },
        tablePaginationInfo: {
            display: 'flex',
            justifyContent: 'space-between',
            padding: '10px',
        },

        container: {
            display: 'flex',
            width: '100%',
            backgroundColor: 'white',
            justifyContent: 'center',
        },
        fab: {
            position: 'absolute',
            bottom: 55,
            left: 'calc( 100vw - 100px)',
            zIndex: 3,
            backgroundColor: 'white',
        },
        countSpan: {
            color: '#484848',
            fontSize: 14,
            letterSpacing: 0.1,
            display: 'flex',
            alignItems: 'center',
        },
        metadataContainer: {
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '10px',
        },
        emptyInboxList: {
            display: 'flex',
            alignItems: 'center',
            height: '100%',
        },
    }),
);

const columns: IColumn[] = [
    {
        id: 'formName|string',
        label: 'Form Name',
        align: 'left',
        maxWidth: 300,
        field: 'formName',
        sortFields: ['formName'],
    },
    {
        id: 'type|string',
        label: 'Request Type',
        align: 'left',
        maxWidth: 300,
        field: 'type',
        sortFields: ['type'],
    },
    {
        id: 'submissionDate|date',
        label: 'Date Received',
        align: 'left',
        maxWidth: 300,
        field: 'submissionDate',
        sortFields: ['submissionDate'],
    },
    {
        id: 'context.person.name|string',
        label: 'Applicant Name',
        align: 'left',
        maxWidth: 300,
        field: 'context.person.name',
        sortFields: ['context.person.name'],
    },
    {
        id: 'context.license.licenseNumber|string',
        label: 'License Number',
        align: 'left',
        maxWidth: 300,
        field: 'context.license.licenseNumber',
        sortFields: ['context.license.licenseNumber'],
    },
    {
        id: 'status|string',
        label: 'Status',
        align: 'left',
        maxWidth: 300,
        field: 'status',
        sortFields: ['status'],
    },
];

const InboxInfiniteList: FunctionComponent<InboxInfiniteListProps> = (props) => {
    const classes = componentStyles();
    const history = useHistory();
    const [scrollTop, setScrollTop] = useState<number>(0);
    const [isDenyModalOpen, setIsDenyModalOpen] = useState(false);
    const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
    const [rowId, setRowId] = useState<string>('');

    const {
        inboxList,
        skip,
        inboxCount,
        hasMore,
        setSkip,
        sortBy,
        sortOrder,
        setSortOrder,
        setSortColumn,
        updateInboxStatus,
        isLoading,
    } = props;
    useEffect(() => {
        if (skip === 0) {
            setScrollTop(0);
        }
    }, [skip]);

    const loadMore = () => {
        if (hasMore) {
            setSkip(skip + 1);
        }
    };
    const routeChange = (path: string) => {
        history.push(path);
    };

    const onClickRow = (id: string, e: any) => {
        const inboxItem = inboxList.find((inboxItem) => inboxItem.id === id);
        e.stopPropagation();
        routeChange(BASE_URL + `/license/${inboxItem?.context.license.id}/detail`);
    };

    const denyActionHandler = (id: string, e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setRowId(id);
        setIsDenyModalOpen(true);
    };

    const dispatchUpdateInboxStatus = (data: UpdateInboxStatus) => {
        updateInboxStatus(rowId, data);
        data.status === 'Approved' ? setIsApproveModalOpen(false) : setIsDenyModalOpen(false);
    };

    const approveActionHandler = (id: string, e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setRowId(id);
        setIsApproveModalOpen(true);
    };

    return (
        <Box className={`${classes.root} verticalFillContainer`}>
            <Box className={classes.metadataContainer}>
                <Button variant="contained" color="primary" disabled style={{ display: 'none' }}>
                    <AddIcon />
                    ADD REQUEST
                </Button>
            </Box>
            <Box className={classes.tablePaginationInfo}>
                <span className={classes.countSpan}>{`${inboxCount.totalInboxCount} Results`}</span>
            </Box>
            <Box className={`${classes.container} verticalFillContent`}>
                {inboxList.length > 0 ? (
                    <InfiniteScrollTableView
                        columns={columns}
                        data={inboxList}
                        count={inboxCount.totalInboxCount}
                        onClickRow={onClickRow}
                        hasMore={hasMore}
                        loadMore={loadMore}
                        sortOrder={sortOrder}
                        sortBy={sortBy}
                        setSortColumn={setSortColumn}
                        setSortOrder={setSortOrder}
                        actionFlag
                        denyActionHandler={denyActionHandler}
                        approveActionHandler={approveActionHandler}
                    />
                ) : (
                    <Box className={classes.emptyInboxList}>
                        <TableEmptyState
                            title="No Items Found"
                            message="All requests will be stored here"
                        ></TableEmptyState>
                    </Box>
                )}
            </Box>

            {scrollTop > 20 && (
                <Tooltip title="Back to Top" placement="top">
                    <Fab
                        size="small"
                        className={classes.fab}
                        aria-label="back-to-top"
                        onClick={() => {
                            setScrollTop(0);
                        }}
                    >
                        <BackToTopArrow color="primary" />
                    </Fab>
                </Tooltip>
            )}
            {isLoading && <Loader />}

            {isDenyModalOpen && (
                <Modal open maxWidth={'xs'} fullWidth>
                    <DeleteDialog
                        onClose={() => setIsDenyModalOpen(false)}
                        onDelete={() => dispatchUpdateInboxStatus({ status: 'Denied' })}
                        contentTitle="Deny this request?"
                        content="This cannot be changed."
                        buttonContent="Deny"
                    />
                </Modal>
            )}
            {isApproveModalOpen && (
                <Modal open maxWidth={'xs'} fullWidth>
                    <ConfirmationDialog
                        onClose={() => {
                            setIsApproveModalOpen(false);
                        }}
                        onDismiss={() => dispatchUpdateInboxStatus({ status: 'Approved' })}
                        contentTitle="Approve this request?"
                        content="This cannot be changed."
                        buttonContent="Approve"
                    />
                </Modal>
            )}
        </Box>
    );
};

export default InboxInfiniteList;
